import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const Auth = () => {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await register(formData.email, formData.password, formData.full_name);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 to-accent-light flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-2 border-black shadow-[8px_8px_0px_0px_#000]">
        <CardHeader>
          <img 
            src="https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/wdpxrhtp_35A5B7C9-E791-468D-BB13-60EF0C8B0415.png" 
            alt="Savage Rejuvenation" 
            className="h-16 w-auto mx-auto mb-4"
          />
          <CardTitle className="font-barlow text-2xl text-center uppercase">Dashboard Access</CardTitle>
          <CardDescription className="text-center">Growth Operating System</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="login">Login</TabsTrigger>
              <TabsTrigger value="register">Register</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block font-manrope font-semibold mb-2">Email</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="border-2 border-black"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block font-manrope font-semibold mb-2">Password</label>
                  <Input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="border-2 border-black"
                    placeholder="••••••••"
                  />
                </div>
                {error && <p className="text-red-600 text-sm font-semibold">{error}</p>}
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000]"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block font-manrope font-semibold mb-2">Full Name</label>
                  <Input
                    type="text"
                    required
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    className="border-2 border-black"
                    placeholder="Jason or Lainie"
                  />
                </div>
                <div>
                  <label className="block font-manrope font-semibold mb-2">Email</label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="border-2 border-black"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block font-manrope font-semibold mb-2">Password</label>
                  <Input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="border-2 border-black"
                    placeholder="••••••••"
                    minLength={6}
                  />
                </div>
                {error && <p className="text-red-600 text-sm font-semibold">{error}</p>}
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000]"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
